import { Grid, makeStyles } from '@material-ui/core'
import AddOutlined from '@material-ui/icons/AddOutlined'
import EditOutlined from '@material-ui/icons/EditOutlined'
import DependentInput from 'elements/input/DependentInput'
import { List } from 'elements/react-admin/List'
import ListActions from 'elements/react-admin/ListActions'
import { ComponentVersions_3_0, ComponentVersionsInherit, Typography } from 'opensolar-ui'
import { useEffect } from 'react'
import {
  BooleanInput,
  Button,
  Create,
  Datagrid,
  Edit,
  FunctionField,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  useNotify,
  useRedirect,
} from 'react-admin'
import { useHistory } from 'react-router-dom'
import { getPostSearchParams } from 'util/misc'
import { FINANCE_CTA_TYPE_CHOICES, FinanceCtaType } from './constants'

const useStyles = makeStyles(() => ({
  row: {
    display: 'flex',
    alignItems: 'center',
  },
}))

const getFinancePartnerId = () => {
  const params = getPostSearchParams()
  const paramStr = params?.get('finance_partner_id')
  return paramStr ? parseInt(paramStr) : undefined
}

export const FinanceCTAList = (props) => {
  const classes = useStyles()
  const history = useHistory()
  const financePartnerId = getFinancePartnerId()

  useEffect(() => {
    if (!financePartnerId) {
      history.push('/finance_partners')
    }
  }, [financePartnerId])

  const CustomCreateButton = () => (
    <Button
      label="Create a Finance CTA"
      onClick={() => history.push(`/finance_ctas/create?finance_partner_id=${financePartnerId}`)}
      startIcon={<AddOutlined />}
      color="inherit"
      size="large"
    />
  )

  const goToEdit = (record) => {
    history.push(`/finance_ctas/${record.id}?finance_partner_id=${financePartnerId}`)
  }
  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <List
        hasSearch={true}
        actions={<ListActions CustomButton={CustomCreateButton()} />}
        {...props}
        hasCreate={false}
        filter={{ finance_partner_id: financePartnerId }}
      >
        <Datagrid>
          <TextField source="title" />
          <TextField source="finance_partner_integration_name" label="Integration" />
          <FunctionField
            source="type"
            label="Type"
            sortable={false}
            render={(record: any, source, rest) => <div>{record.type?.replaceAll('_', ' ')}</div>}
          />
          <FunctionField
            source="finance_product"
            label="Actions"
            render={(record: any, source, rest) => (
              <div className={classes.row}>
                <Button startIcon={<EditOutlined />} label="Edit" onClick={() => goToEdit(record)} color="inherit" />
                {/* <EditButton useListStyle={true} record={record} basePath={rest.basePath} /> */}
              </div>
            )}
            sortable={false}
          />
        </Datagrid>
      </List>
    </ComponentVersionsInherit>
  )
}

const FinanceCTAFields = (props) => {
  const financePartnerId = getFinancePartnerId()
  const validateJSON = (value) => {
    if (!value) return undefined
    try {
      JSON.parse(value)
      return undefined
    } catch (ex) {
      return 'Invalid JSON'
    }
  }
  return (
    <Grid container spacing={3}>
      <Grid item xs={3}>
        <TextInput source="title" validate={[required()]} fullWidth />
      </Grid>
      <Grid item xs={3}>
        <TextInput source="internal_name" validate={[required()]} fullWidth />
      </Grid>
      <Grid item xs={4}>
        <SelectInput
          source="type"
          choices={FINANCE_CTA_TYPE_CHOICES}
          fullWidth={true}
          style={{ width: '100%' }}
          optionText="label"
          validate={[required()]}
        />
      </Grid>
      <Grid item xs={2}>
        <NumberInput source="priority" fullWidth />
      </Grid>
      <Grid item xs={6}>
        <TextInput source="cta_main_text" fullWidth />
      </Grid>
      <Grid item xs={6}>
        <TextInput source="cta_secondary_text" fullWidth />
      </Grid>
      <Grid item xs={3}>
        <BooleanInput source="is_customer_facing" fullWidth />
      </Grid>
      <Grid item xs={3}>
        <BooleanInput source="is_pro_facing" fullWidth />
      </Grid>
      <Grid item xs={3}>
        <BooleanInput source="has_api_side_effect" fullWidth />
      </Grid>
      <Grid item xs={3}>
        <BooleanInput source="is_archived" fullWidth />
      </Grid>
      <DependentInput dependsOn="type" value={FinanceCtaType.PROPOSAL_TOOLBAR_DIALOG}>
        <>
          <Grid item xs={3}>
            <BooleanInput source="include_for_all_commercial" fullWidth />
          </Grid>
          <Grid item xs={3}>
            <BooleanInput source="include_for_all_residential" fullWidth />
          </Grid>
          <Grid item xs={3}>
            <BooleanInput source="include_logo" fullWidth />
          </Grid>
        </>
      </DependentInput>
      <Grid item xs={4}>
        {/* @ts-ignore */}
        <ReferenceInput
          label="Finance Partner"
          reference="finance_partners"
          source="finance_partner"
          fullWidth
          style={{ width: '100%' }}
          disabled={true}
        >
          <SelectInput optionText="display_name" optionValue="url" source="finance_partner" disabled={true} />
        </ReferenceInput>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2SemiBold">Status to Mapping JSON</Typography>
        <Typography variant="body2">
          Used to map the transaction's status to a the status of the CTA. An example of this would be to say "Approved"
          transactions should result in "Completed" CTAs
        </Typography>
        <TextInput
          source="status_mapping_json"
          fullWidth
          validate={validateJSON}
          defaultValue={'{}'}
          multiline={true}
          rows={10}
          InputProps={{
            classes: { input: 'code-block' },
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2SemiBold">Status to CTA Description JSON</Typography>
        <Typography variant="body2">
          Used to map the transaction's status to a descriptin that appears with the CTA. An example of this would be
          showing some text below the application CTA when a customer has been denied.
        </Typography>
        <TextInput
          source="status_to_description_json"
          fullWidth
          validate={validateJSON}
          defaultValue={'{}'}
          multiline={true}
          rows={10}
          InputProps={{
            classes: { input: 'code-block' },
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2SemiBold">Status to CTA Text JSON</Typography>
        <Typography variant="body2">
          Used to map the transaction's status to a text that appears on the CTA button/text. An example of this would
          be showing "View Application" when a transaction is in a "Pending" status instead of showing "Apply Now".
        </Typography>
        <TextInput
          source="status_to_cta_text_json"
          fullWidth
          validate={validateJSON}
          defaultValue={'{}'}
          multiline={true}
          rows={5}
          InputProps={{
            classes: { input: 'code-block' },
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2SemiBold">Iframe URL Configuration JSON</Typography>
        <Typography variant="body2">
          Used to dynamically create the iframe URL for a given system/payment option. Only necessary when we don't need
          to call out to the lender to create the iframe url first.
        </Typography>
        <TextInput
          source="iframe_url_configuration_json"
          defaultValue={'{}'}
          fullWidth
          validate={validateJSON}
          multiline={true}
          rows={30}
          InputProps={{
            classes: { input: 'code-block' },
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2SemiBold">Hosted Form JSON</Typography>
        <Typography variant="body2">
          Used to dynamically create the hosted credit application form URL for a given system/payment option.
        </Typography>
        <TextInput
          source="form_json"
          defaultValue={'{}'}
          fullWidth
          validate={validateJSON}
          multiline={true}
          rows={30}
          InputProps={{
            classes: { input: 'code-block' },
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2SemiBold">API Side Effect Configuration</Typography>
        <Typography variant="body2">Used to dynamically configure the API side effect (if enabled).</Typography>
        <TextInput
          source="api_side_effect_config_json"
          defaultValue={'{}'}
          fullWidth
          validate={validateJSON}
          multiline={true}
          rows={30}
          InputProps={{
            classes: { input: 'code-block' },
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body2SemiBold">CTA Dialog Config JSON</Typography>
        <Typography variant="body2">
          Used to dynamically configure Dialog that opens from a CTA with a type of "Proposal Toolbar Dialog".
        </Typography>
        <TextInput
          source="cta_dialog_config_json"
          defaultValue={'{}'}
          fullWidth
          validate={validateJSON}
          multiline={true}
          rows={30}
          InputProps={{
            classes: { input: 'code-block' },
          }}
        />
      </Grid>
    </Grid>
  )
}

export const FinanceCTAEdit = (props) => {
  const financePartnerId = getFinancePartnerId()

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <Edit
        {...props}
        hasDelete={true}
        redirect={(resource, id, data) => `/finance_ctas?finance_partner_id=${financePartnerId}`}
      >
        <SimpleForm>
          <FinanceCTAFields {...props} />
        </SimpleForm>
      </Edit>
    </ComponentVersionsInherit>
  )
}

export const FinanceCTACreate = (props) => {
  const financePartnerId = getFinancePartnerId()
  const redirect = useRedirect()
  const notify = useNotify()

  const onSuccess = (data) => {
    notify('Financing Product Created')
    redirect(`/finance_ctas?finance_partner_id=${financePartnerId}`)
  }
  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <Create {...props} mutationOptions={{ onSuccess }}>
        <SimpleForm initialValues={{ finance_partner_id: financePartnerId }}>
          <FinanceCTAFields {...props} />
        </SimpleForm>
      </Create>
    </ComponentVersionsInherit>
  )
}
